import {assignSafely, getSafely} from 'shared/web';

const PLURAL_FORM_KEYS = ['zero', 'one', 'two', 'few', 'many', 'other'] as const;

function isPluralFormObject(value: unknown): value is Record<string, string | null> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj);
  const hasPluralKey = keys.some((key) =>
    PLURAL_FORM_KEYS.includes(key as (typeof PLURAL_FORM_KEYS)[number])
  );
  const allStringOrNull = keys.every((key) => {
    const v = getSafely(obj, key);
    return typeof v === 'string' || v == null;
  });
  return hasPluralKey && allStringOrNull;
}

/**
 * Ensures plural form objects have `few` and `many` keys when missing,
 * using the value of `other`. Used for the fallback locale (e.g. en) so that
 * when CZ/PL/SK request a "few" or "many" form and fall back to the fallback locale,
 * they receive a string (the "other" text) instead of undefined, avoiding "[object Object]".
 */
export function normalizePluralFallback<T extends Record<string, unknown>>(translation: T): T {
  const result = {} as T;
  for (const key of Object.keys(translation) as (keyof T)[]) {
    const value = getSafely(translation, key);
    if (isPluralFormObject(value)) {
      const otherValue = value.other;
      const normalized = {
        ...value,
        ...(value.few == null && otherValue != null && {few: otherValue}),
        ...(value.many == null && otherValue != null && {many: otherValue}),
      } as T[keyof T];
      assignSafely(result, key as string, normalized);
    } else {
      assignSafely(result, key as string, value);
    }
  }
  return result;
}
